import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { Tabs } from "../../../../ui/components/Tabs";
import { getCompany } from "../../data/api/companyApi";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";
import { CompanyMapper } from "../../data/mappers/CompanyMapper";
import { CompanyTeamTab } from "../components/CompanyTeamTab";
import { FeatherArrowLeft } from "@subframe/core";

interface CompanyUser {
  id: number;
  email: string;
  userName?: string;
  is_active: boolean;
  is_master_admin: boolean;
  role_name: string | null;
  is_company_admin: boolean;
}

/** The onboarding draft this company grew out of, when it had one. */
interface CompanyOnboarding {
  public_id: string;
  name: string;
  status: string;
  live_at: string | null;
}

export const CompanyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const companyId = id ? parseInt(id, 10) : null;
  const [company, setCompany] = useState<CompanyEntity | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "users" | "team">(
    "overview"
  );
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [onboarding, setOnboarding] = useState<CompanyOnboarding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompanyData = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getCompany(companyId);
      const companyEntity = CompanyMapper.toCompanyEntity(
        response.data.company
      );
      setCompany(companyEntity);
      setUsers(response.data.users);
      setOnboarding(response.data.onboarding ?? null);
    } catch (err: any) {
      setError(err.message || "Failed to load company data");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadCompanyData();
    }
  }, [companyId, loadCompanyData]);

  if (loading) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading company...</p>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{error || "Company not found"}</p>
          <Button
            variant="neutral-secondary"
            onClick={() => navigate("/admin/dashboard?tab=companies")}
            className="mt-4"
          >
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const isLiveOnboardedClient = Boolean(onboarding && onboarding.live_at);

  const getLoginScope = (user: CompanyUser) => {
    if (user.role_name === "client_view") {
      return "Company Subdomain";
    }
    return "Zournal HQ";
  };

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start self-stretch bg-neutral-50 overflow-auto">
      <div className="flex w-full flex-col items-start bg-white">
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-secondary"
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/admin/dashboard?tab=companies")}
            >
              Back
            </Button>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {company.company_name}
              </span>
              <span className="text-body font-body text-subtext-color">
                {company.app_url}.zournalhq.com
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={company.is_active ? "success" : "error"}>
              {company.is_active ? "Active" : "Inactive"}
            </Badge>
            {/* <Button
              variant="destructive-secondary"
              icon={<FeatherPause />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
            >
              Suspend Company
            </Button> */}
          </div>
        </div>
        <Tabs className="px-8">
          <Tabs.Item
            active={activeTab === "overview"}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "users"}
            onClick={() => setActiveTab("users")}
          >
            Users & Roles
          </Tabs.Item>
          {/* Only a company that came from onboarding AND went live has a
              tenant team board to read back. */}
          {isLiveOnboardedClient && (
            <Tabs.Item
              active={activeTab === "team"}
              onClick={() => setActiveTab("team")}
            >
              Team
            </Tabs.Item>
          )}
        </Tabs>
      </div>

      {activeTab === "overview" && (
        <div className="flex w-full flex-col items-start px-8 py-6">
          <div className="flex w-full max-w-[768px] flex-col items-start gap-6 rounded-md border border-solid border-neutral-border bg-white px-6 py-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Company Information
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-start gap-8">
                <div className="flex w-48 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Company Name
                  </span>
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start">
                  <span className="text-body font-body text-neutral-600">
                    {company.company_name}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-start gap-8">
                <div className="flex w-48 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Subdomain
                  </span>
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start">
                  <span className="text-body font-body text-neutral-600">
                    {company.app_url}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-start gap-8">
                <div className="flex w-48 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Company ID
                  </span>
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start">
                  <span className="text-body font-body text-neutral-600">
                    {company.id}
                  </span>
                </div>
              </div>
              {company.created_at && (
                <div className="flex w-full items-start gap-8">
                  <div className="flex w-48 flex-none flex-col items-start">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Created On
                    </span>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="text-body font-body text-neutral-600">
                      {formatDate(company.created_at)}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex w-full items-start gap-8">
                <div className="flex w-48 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Current Status
                  </span>
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-start">
                  <Badge variant={company.is_active ? "success" : "error"}>
                    {company.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "team" && isLiveOnboardedClient && (
        <CompanyTeamTab
          companyId={company.id}
          companyAppUrl={company.app_url}
        />
      )}

      {activeTab === "users" && (
        <div className="flex w-full flex-col items-start gap-10 px-8 py-6">
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Users & Roles
                </span>
                <span className="text-body font-body text-subtext-color">
                  Manage user access and permissions for this company
                </span>
              </div>
              {/* <Button
                icon={<FeatherUserPlus />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
              >
                Assign User
              </Button> */}
            </div>
            {error && (
              <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <span className="text-sm font-medium text-red-800">
                  {error}
                </span>
              </div>
            )}
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>User Name</Table.HeaderCell>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Role</Table.HeaderCell>
                  <Table.HeaderCell>Login Scope</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Action
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {users.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} className="text-center py-8">
                    <span className="text-body font-body text-subtext-color">
                      No users assigned to this company
                    </span>
                  </Table.Cell>
                </Table.Row>
              ) : (
                users.map((user) => (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {user.userName || user.email.split("@")[0]}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-body font-body text-neutral-500">
                        {user.email}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {user.is_company_admin ? (
                        <Badge variant="neutral">Company Admin</Badge>
                      ) : user.role_name ? (
                        <Badge variant="brand">{user.role_name}</Badge>
                      ) : (
                        <Badge>No Role</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-caption font-caption text-neutral-500">
                        {getLoginScope(user)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={user.is_active ? "success" : "neutral"}>
                        {user.is_active ? "Active" : "Suspended"}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* <Button
                            variant="neutral-tertiary"
                            size="small"
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>
                            ) => {}}
                          >
                            Change Role
                          </Button>
                          <Button
                            variant="destructive-tertiary"
                            size="small"
                            icon={<FeatherTrash />}
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>
                            ) => {}}
                          /> */}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
